import {generateUUID} from "../../../helpers/utils";

export const AGENTIC_SECTION_CITATION_MARKER_REGEX = /\[\[cite:([^\]]+)\]\]/g;

function normalizePerspectiveAuthor(author) {
    if (!author || typeof author !== "object") return null;

    return {
        uid: typeof author.uid === "string" ? author.uid.trim() : "",
        displayName: typeof author.displayName === "string" ? author.displayName.trim() : "",
        email: typeof author.email === "string" ? author.email.trim() : "",
        headshot: typeof author.headshot === "string" ? author.headshot.trim() : "",
        role: typeof author.role === "string" ? author.role.trim() : "",
    };
}

function buildAuthorIdentity(author = {}) {
    return author.uid || author.email || author.displayName || "";
}

function normalizeCitationEntry(entry) {
    if (!entry || typeof entry !== "object") return null;

    const citationId = typeof entry.citationId === "string" ? entry.citationId.trim() : "";
    if (!citationId) return null;

    const normalizedReport = entry.report && typeof entry.report === 'object'
        ? {
            id: typeof entry.report.id === 'string' ? entry.report.id.trim() : '',
            title: typeof entry.report.title === 'string' ? entry.report.title.trim() : '',
        }
        : null;

    return {
        citationId,
        docId: typeof entry.docId === "string" ? entry.docId.trim() : "",
        report: normalizedReport,
        title: typeof entry.title === "string" ? entry.title.trim() : "",
        authors: Array.isArray(entry.authors)
            ? entry.authors.map(normalizePerspectiveAuthor).filter(Boolean)
            : [],
        excerpt: typeof entry.excerpt === "string" ? entry.excerpt.trim() : "",
        indexPath: typeof entry.indexPath === "string" ? entry.indexPath.trim() : "",
    };
}

function collectUniqueAuthors(citations = []) {
    const byIdentity = new Map();

    citations.forEach((citation) => {
        (citation?.authors || []).forEach((author) => {
            const identity = buildAuthorIdentity(author);
            if (!identity || byIdentity.has(identity)) return;
            byIdentity.set(identity, author);
        });
    });

    return Array.from(byIdentity.values());
}

function normalizeStructuredResponsePayload(responseObject) {
    if (!responseObject) return "";
    if (typeof responseObject === "string") return responseObject.trim();

    if (typeof responseObject === "object") {
        try {
            return JSON.stringify(responseObject);
        } catch (_error) {
            return "";
        }
    }

    return "";
}

function parseStructuredResponseData(rawPayload) {
    const normalizedPayload = normalizeStructuredResponsePayload(rawPayload);
    if (!normalizedPayload) return null;

    try {
        const parsedPayload = JSON.parse(normalizedPayload);
        const payloadData = parsedPayload?.data;
        if (payloadData && typeof payloadData === "object" && !Array.isArray(payloadData)) {
            const markdown = typeof payloadData.markdown === "string" ? payloadData.markdown.trim() : "";
            const citations = Array.isArray(payloadData.citations)
                ? payloadData.citations.map(normalizeCitationEntry).filter(Boolean)
                : [];

            if (markdown) {
                return {
                    responseIndex: 0,
                    text: markdown,
                    markdown,
                    citations,
                    authors: collectUniqueAuthors(citations),
                };
            }
        }
    } catch (_error) {
        // Fall back to other response formats below.
    }

    return null;
}

export function stripAgenticSectionCitationMarkers(markdown = "") {
    if (typeof markdown !== "string" || !markdown) return "";

    return markdown
        .replace(AGENTIC_SECTION_CITATION_MARKER_REGEX, "")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export function parseAgenticSectionResponseData(responseText, responseObject = "") {
    const structuredResponseData = parseStructuredResponseData(responseObject);
    if (structuredResponseData) {
        return structuredResponseData;
    }

    const normalizedResponseText = normalizePromptText(responseText).trim();
    if (!normalizedResponseText) {
        return {
            responseIndex: 0,
            text: "",
            markdown: "",
            citations: [],
            authors: [],
        };
    }

    return {
        responseIndex: 0,
        text: normalizedResponseText,
        markdown: normalizedResponseText,
        citations: [],
        authors: [],
    };
}

export const AGENTIC_SECTION_RESPONSE_STATUS = Object.freeze({
    IDLE: "idle",
    QUEUED: "queued",
    GENERATING: "generating",
    READY: "ready",
    ERROR: "error",
});

export const DEFAULT_SECTION_STATE = Object.freeze({
    sectionId: null,
    promptText: "",
    selectedAgent: "",
    requestId: null,
    responseText: "",
    responseObject: "",
    responseStatus: AGENTIC_SECTION_RESPONSE_STATUS.IDLE,
    responseUpdatedAt: null,
    errorMessage: "",
    lastSubmittedPromptText: "",
    lastSubmittedAgent: "",
    createdAt: null,
    updatedAt: null,
});

export function normalizePromptText(promptText) {
    return typeof promptText === "string" ? promptText : "";
}

export function normalizeSelectedAgent(selectedAgent) {
    return typeof selectedAgent === "string" ? selectedAgent.trim() : DEFAULT_SECTION_STATE.selectedAgent;
}

export function getAgenticSectionStateFromAttrs(attrs) {
    const safeState = {
        ...DEFAULT_SECTION_STATE,
        ...(attrs || {}),
    };

    return {
        ...safeState,
        promptText: normalizePromptText(safeState.promptText),
        selectedAgent: normalizeSelectedAgent(safeState.selectedAgent),
        responseText: normalizePromptText(safeState.responseText),
        responseObject: normalizeStructuredResponsePayload(safeState.responseObject),
        responseStatus: safeState.responseStatus || AGENTIC_SECTION_RESPONSE_STATUS.IDLE,
        errorMessage: normalizePromptText(safeState.errorMessage),
        lastSubmittedPromptText: normalizePromptText(safeState.lastSubmittedPromptText),
        lastSubmittedAgent: normalizeSelectedAgent(safeState.lastSubmittedAgent),
    };
}

export function ensureAgenticSectionId(attrs) {
    return attrs?.sectionId || generateUUID();
}

export function canSubmitAgenticSection(sectionState, {
    promptText = sectionState?.promptText,
    selectedAgent = sectionState?.selectedAgent,
} = {}) {
    const normalizedPromptText = normalizePromptText(promptText);
    const normalizedAgent = normalizeSelectedAgent(selectedAgent);
    const responseStatus = sectionState?.responseStatus || AGENTIC_SECTION_RESPONSE_STATUS.IDLE;
    const isSubmitting = [
        AGENTIC_SECTION_RESPONSE_STATUS.QUEUED,
        AGENTIC_SECTION_RESPONSE_STATUS.GENERATING,
    ].includes(responseStatus);
    const unchanged = normalizedPromptText === (sectionState?.lastSubmittedPromptText || "")
        && normalizedAgent === normalizeSelectedAgent(sectionState?.lastSubmittedAgent);

    return Boolean(normalizedPromptText.trim()) && Boolean(normalizedAgent) && !isSubmitting && !unchanged;
}

export function getAgenticSectionViewState(sectionState) {
    const safeState = getAgenticSectionStateFromAttrs(sectionState);
    const isSubmitting = [
        AGENTIC_SECTION_RESPONSE_STATUS.QUEUED,
        AGENTIC_SECTION_RESPONSE_STATUS.GENERATING,
    ].includes(safeState.responseStatus);
    const responseData = parseAgenticSectionResponseData(safeState.responseText, safeState.responseObject);

    return {
        ...safeState,
        responseData,
        isSubmitting,
        canSubmit: canSubmitAgenticSection(safeState),
    };
}

export function buildAgenticSectionDraftPatch(currentState, partialDraft = {}) {
    const now = Date.now();
    return {
        sectionId: ensureAgenticSectionId(currentState),
        promptText: Object.prototype.hasOwnProperty.call(partialDraft, "promptText")
            ? normalizePromptText(partialDraft.promptText)
            : normalizePromptText(currentState?.promptText),
        selectedAgent: Object.prototype.hasOwnProperty.call(partialDraft, "selectedAgent")
            ? normalizeSelectedAgent(partialDraft.selectedAgent)
            : normalizeSelectedAgent(currentState?.selectedAgent),
        updatedAt: now,
        createdAt: currentState?.createdAt || now,
    };
}

export function buildQueuedAgenticSectionPatch(currentState, {promptText, selectedAgent} = {}) {
    const now = Date.now();
    const normalizedPromptText = normalizePromptText(promptText);
    const normalizedSelectedAgent = normalizeSelectedAgent(selectedAgent);

    if (!normalizedPromptText.trim()) {
        throw new Error("[agenticSectionState] promptText is required");
    }
    if (!normalizedSelectedAgent) {
        throw new Error("[agenticSectionState] selectedAgent is required");
    }

    return {
        sectionId: ensureAgenticSectionId(currentState),
        promptText: normalizedPromptText,
        selectedAgent: normalizedSelectedAgent,
        requestId: generateUUID(),
        responseText: "",
        responseObject: "",
        responseStatus: AGENTIC_SECTION_RESPONSE_STATUS.QUEUED,
        responseUpdatedAt: now,
        errorMessage: "",
        lastSubmittedPromptText: normalizedPromptText,
        lastSubmittedAgent: normalizedSelectedAgent,
        updatedAt: now,
        createdAt: currentState?.createdAt || now,
    };
}

export function getAgenticSectionSuggestionText(sectionState) {
    const safeState = getAgenticSectionStateFromAttrs(sectionState);
    const responseData = parseAgenticSectionResponseData(safeState.responseText, safeState.responseObject);
    return stripAgenticSectionCitationMarkers(responseData.markdown || responseData.text || "");
}

